/* eslint-disable prettier/prettier */
import { createStore } from '@reduxjs/toolkit';

import { SortOrder } from 'core/util/Search';
import { Queue, RootCause, Tag } from 'tickets/actions2/common/actions-api';
import { FieldName, File } from 'tickets/actions2/common/ticket-constants';
import { Ticket } from 'tickets/api/Tickets.types';

import { Option } from './action.types';

export const ActionStoreReducerActionConstants: { [key: string]: string } = {
    UPDATE_TICKET: 'UPDATE_TICKET',
    SET_SELECTED_TICKET: 'SET_SELECTED_TICKET',
    SET_SELECTED_TICKET_ID: 'SET_SELECTED_TICKET_ID',
    SET_TICKETS_LIST: 'SET_TICKETS_LIST',
    SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
    TICKET_COUNT: 'TICKET_COUNT',
    PAGE_NUMBER: 'PAGE_NUMBER',
    TOGGLE_SELECTED_TICKET_IDS: 'TOGGLE_SELECTED_TICKET_IDS',
    TOGGLE_SELECTED_ALL: 'TOGGLE_SELECTED_ALL',
    SET_STAGES: 'SET_STAGES',
    SET_QUEUES: 'SET_QUEUES',
    SET_LOCATIONS: 'SET_LOCATIONS',
    SET_TAGS: 'SET_TAGS',
    SET_ROOT_CAUSES: 'SET_ROOT_CAUSES',
    SET_CLOSING_REASONS: 'SET_CLOSING_REASONS',
    SET_PAGE_INDEX: 'SET_PAGE_INDEX',
    SET_ARGS: 'SET_ARGS',
    SET_PAGE_SIZE: 'SET_PAGE_SIZE',
    SET_LIST_LOADING: 'SET_LIST_LOADING',
    SET_SORT_BY: 'SET_SORT_BY',
    REFETCH_TICKETS: 'REFETCH_TICKETS',
    SET_TICKET_ATTACHMENTS: 'SET_TICKET_ATTACHMENTS',
    SET_TICKET_SEARCH_QUERY: 'SET_TICKET_SEARCH_QUERY',
    RESET_TICKET_SEARCH_QUERY: 'RESET_TICKET_SEARCH_QUERY',
    RESET_TICKETS_LIST: 'RESET_TICKETS_LIST',
    SHOW_CREATE_TICKET: 'SHOW_CREATE_TICKET',
    HIDE_CREATE_TICKET: 'HIDE_CREATE_TICKET',
    SET_IS_MOBILE: 'SET_IS_MOBILE',
    APPEND_TICKETS_LIST: 'APPEND_TICKETS_LIST',
    SET_USER_PREFERENCES: 'SET_USER_PREFERENCES',

    // Other than Tickets
    REFETCH_REVIEW: 'REFETCH_REVIEW',
};

type TicketActionType = {
    payload: any;
    type: string;
};

export type TicketsState = {
    ticketList: Ticket[];
    selectedTicket: Ticket | null;
    selectedTicketId: number | null;
    searchQuery: string;
    count: number;
    metrics: any;
    pageNumber: 1;
    selectedTicketIds: number[];
    stages: Option[];
    queues: Queue[];
    locations: { [key: string]: any };
    tags: Tag[];
    rootCauses: RootCause[];
    closingReasons: any[];
    pageIndex: number;
    listLoading: boolean;
    sortBy: any;
    refetchInc: number;
    refetchReview: number;
    attachments?: File[];
    pageSize: number;
    ticketSearchQuery: {
        value: string;
        key: string;
    } | null;
    showCreateTicket: boolean;
    args: any;
    isMobile: boolean;
    userPreferences: {
        viewType: string;
        pageSize: number;
    };
};

export const initialArgs = {
    includeMacros: false,
    includeCredentials: false,
    includeReviewResponses: false,
    includeFlags: false,
    includePageSummaries: false,
    includeNotesCount: false,
    includeNotes: false,
    isExternalView: false,
    includeUsers: true,
    includeReviews: true,
    includeSurveyTemplates: true,
    isActionPage: true,
};

const ticketSortBy = JSON.parse(localStorage.getItem('ticketSortBy') || '{}');
const initialState: TicketsState = {
    ticketList: [
        // Your existing data structure
    ],
    selectedTicket: null,
    selectedTicketId: null,
    searchQuery: '',
    count: 0,
    metrics: {},
    pageNumber: 1,
    selectedTicketIds: [],
    stages: [],
    queues: [],
    locations: {},
    tags: [],
    rootCauses: [],
    pageIndex: 0,
    args: {
        ...initialArgs,
    },
    listLoading: false,
    sortBy: ticketSortBy?.name
        ? ticketSortBy
        : {
              name: FieldName.DueDate,
              sortOrder: SortOrder.Asc,
          },
    refetchInc: 0,
    refetchReview: 0,
    attachments: [],
    pageSize: 10,
    ticketSearchQuery: null,
    showCreateTicket: false,
    closingReasons: [],
    isMobile: false,
    userPreferences: {
        viewType: 'THREE_PANEL',
        pageSize: 10,
    },
};

const reducer: any = (state: TicketsState = initialState, action: TicketActionType) => {
    switch (action.type) {
        case ActionStoreReducerActionConstants.UPDATE_TICKET:
            // eslint-disable-next-line
            const tickets: Ticket[] = [];
            state.ticketList?.forEach((ticket: Ticket) => {
                const hasUpdatedTicket = action.payload?.find(
                    (updatedTicket: Ticket) => updatedTicket.id === ticket.id,
                );

                if (hasUpdatedTicket) {
                    tickets.push(hasUpdatedTicket);
                } else {
                    tickets.push(ticket);
                }
            });

            return {
                ...state,
                ticketList: tickets,
                selectedTicket: action.payload[0] || null,
            };
        case ActionStoreReducerActionConstants.SET_SELECTED_TICKET:
            return {
                ...state,
                selectedTicket: action.payload,
                attachments: [],
                showCreateTicket: false,
                selectedTicketId: action.payload.id,
            };
        case ActionStoreReducerActionConstants.SET_SELECTED_TICKET_ID:
            return { ...state, selectedTicketId: action.payload };
        case ActionStoreReducerActionConstants.SET_TICKETS_LIST: {
            const { list, count, metrics, pageIndex, isFilterChanged } = action.payload || {};
            const { ticketList, pageIndex: prevPageIndex, pageSize } = state;
            const lastPageIndex = Math.ceil(count / pageSize) - 1;
            const prevIndex = state.selectedTicketId
                ? ticketList.findIndex((itm: Ticket) => itm.id === state.selectedTicketId)
                : 0;
            const hasFound = list.findIndex((itm: Ticket) => itm.id === state.selectedTicketId);
            const shouldSelectFirstItem = isFilterChanged || (state.selectedTicketIds.length && hasFound === -1);
            const isOnLastPageAndIndexOutOfBounds =
                ((pageIndex ?? prevPageIndex) === lastPageIndex && prevIndex >= list.length) ||
                state.selectedTicket === undefined;

            let selectedTicket: Ticket | undefined;

            if (shouldSelectFirstItem) {
                selectedTicket = list?.[0];
            } else if (isOnLastPageAndIndexOutOfBounds) {
                selectedTicket = undefined;
            } else {
                selectedTicket = list.find((itm: Ticket) => itm.id === state.selectedTicketId) || list?.[prevIndex];
            }

            return {
                ...state,
                ticketList: list || [],
                count: count || 0,
                metrics: metrics || {},
                listLoading: false,
                pageIndex: pageIndex !== undefined ? pageIndex : state.pageIndex,
                selectedTicket,
                selectedTicketIds: [],
                selectedTicketId: selectedTicket?.id,
            };
        }
        case ActionStoreReducerActionConstants.SET_SEARCH_QUERY:
            return { ...state, searchQuery: action.payload };

        case ActionStoreReducerActionConstants.TICKET_COUNT:
            return { ...state, count: state.count + 1 };

        case ActionStoreReducerActionConstants.PAGE_NUMBER:
            return { ...state, pageNumber: action.payload };

        case ActionStoreReducerActionConstants.TOGGLE_SELECTED_TICKET_IDS:
            if (state.selectedTicketIds.includes(action.payload)) {
                return {
                    ...state,
                    selectedTicketIds: state.selectedTicketIds.filter(
                        (existingId: number) => existingId !== action.payload,
                    ),
                };
            }
            return { ...state, selectedTicketIds: [...state.selectedTicketIds, action.payload] };
        case ActionStoreReducerActionConstants.TOGGLE_SELECTED_ALL: {
            return {
                ...state,
                selectedTicketIds: action.payload
                    ? state.ticketList
                          ?.filter((t: Ticket) => !t.isReviewMissingInYeti)
                          .map((ticket: Ticket) => ticket.id)
                    : [],
            };
        }
        case ActionStoreReducerActionConstants.SET_STAGES: {
            return { ...state, stages: [...action.payload] };
        }
        case ActionStoreReducerActionConstants.SET_QUEUES: {
            return { ...state, queues: [...action.payload] };
        }
        case ActionStoreReducerActionConstants.SET_LOCATIONS: {
            return { ...state, locations: { ...state.locations, ...action.payload } };
        }
        case ActionStoreReducerActionConstants.SET_TAGS: {
            return { ...state, tags: [...action.payload] };
        }
        case ActionStoreReducerActionConstants.SET_ROOT_CAUSES: {
            return { ...state, rootCauses: [...action.payload] };
        }
        case ActionStoreReducerActionConstants.SET_PAGE_INDEX: {
            return { ...state, pageIndex: action.payload, listLoading: true, selectedTicket: null }; // setting true assuming everytime page changes list will reload
        }
        case ActionStoreReducerActionConstants.SET_IS_MOBILE: {
            return { ...state, isMobile: action.payload }; // TO KNOW IF ITS MOBILE OR NOT
        }
        case ActionStoreReducerActionConstants.APPEND_TICKETS_LIST: {
            return { ...state, ticketList: [...state.ticketList, ...action.payload], listLoading: false }; // TO KNOW IF ITS MOBILE OR NOT
        }
        case ActionStoreReducerActionConstants.SET_ARGS: {
            return { ...state, args: action.payload, listLoading: true };
        }
        case ActionStoreReducerActionConstants.SET_PAGE_SIZE: {
            return {
                ...state,
                pageSize: action.payload,
                listLoading: true,
                selectedTicket: null,
                pageIndex: 0,
                refetchInc: state.refetchInc + 1,
            }; // setting true assuming everytime page changes list will reload
        }
        case ActionStoreReducerActionConstants.SET_LIST_LOADING: {
            return { ...state, listLoading: action.payload };
        }
        case ActionStoreReducerActionConstants.SET_SORT_BY: {
            let pageIndex: any;

            if (state.isMobile) {
                pageIndex = 0;
            } else {
                pageIndex = state.pageIndex;
            }
            return {
                ...state,
                sortBy: action.payload,
                pageIndex,
                listLoading: true,
                selectedTicketIds: [],
                selectedTicket: null,
            };
        }
        case ActionStoreReducerActionConstants.REFETCH_TICKETS: {
            let pageIndex: any;

            if (state.isMobile) {
                pageIndex = 0;
            } else {
                pageIndex = state.pageIndex;
            }
            return { ...state, refetchInc: state.refetchInc + 1, pageIndex, listLoading: true };
        }
        case ActionStoreReducerActionConstants.SET_TICKET_ATTACHMENTS: {
            return { ...state, attachments: action.payload };
        }
        case ActionStoreReducerActionConstants.SET_TICKET_SEARCH_QUERY: {
            return {
                ...state,
                ticketSearchQuery: action.payload,
                refetchInc: state.refetchInc + 1,
                listLoading: true,
                pageIndex: 0,
            };
        }
        case ActionStoreReducerActionConstants.RESET_TICKET_SEARCH_QUERY: {
            return { ...state, ticketSearchQuery: null };
        }
        case ActionStoreReducerActionConstants.SHOW_CREATE_TICKET: {
            return { ...state, showCreateTicket: true };
        }
        case ActionStoreReducerActionConstants.HIDE_CREATE_TICKET: {
            return { ...state, showCreateTicket: false };
        }
        case ActionStoreReducerActionConstants.SET_CLOSING_REASONS: {
            return { ...state, closingReasons: [...action.payload] };
        }
        case ActionStoreReducerActionConstants.RESET_TICKETS_LIST: {
            return {
                ...state,
                ticketList: [],
                selectedTicketIds: [],
                selectedTicket: null,
                selectedTicketId: null,
                attachments: [],
                listLoading: false,
                pageIndex: 0,
                count: 0,
            };
        }
        case ActionStoreReducerActionConstants.SET_USER_PREFERENCES: {
            return { ...state, pageSize: action.payload.pageSize, userPreferences: { ...action.payload } };
        }
        // Other than tickets
        case ActionStoreReducerActionConstants.REFETCH_REVIEW: {
            return { ...state, refetchInc: state.refetchInc + 1, listLoading: true };
        }

        default:
            return state;
    }
};

export const ticketActionsStore = createStore(reducer);
